import React, { useState, useEffect, useCallback, createContext, useContext } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  CreditCard, 
  Lightning, 
  MagnifyingGlass, 
  Trash, 
  Copy, 
  Sun,
  Moon,
  CheckCircle,
  XCircle,
  Warning,
  Clock,
  Download,
  Play,
  Stop,
  Spinner,
  Storefront,
  Users,
  Trophy,
  Target,
  ChartLine,
  Gift,
  Gear,
  Funnel,
  ShoppingCart,
  GraduationCap,
  UserCircle,
  BracketsSquare,
  TelegramLogo,
  SignOut,
  List,
  ArrowRight,
  Info,
  ShieldWarning,
  Scales
} from "@phosphor-icons/react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("hit_token"));
  const [loading, setLoading] = useState(true);
  const [membershipStatus, setMembershipStatus] = useState(null);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async (retryCount = 0) => {
    try {
      const response = await axios.get(`${API}/auth/me?token=${token}`, { timeout: 10000 });
      setUser(response.data);
      // Check membership after getting user
      if (response.data?.telegram_id) {
        await checkMembership(response.data.telegram_id);
      }
    } catch (error) {
      // Only clear token if it's actually invalid (401 Unauthorized or 404 Not Found)
      if (error.response?.status === 401 || error.response?.status === 404) {
        localStorage.removeItem("hit_token");
        setToken(null);
        setUser(null);
      } else if (retryCount < 2) {
        // Network error or server issue - retry up to 2 times
        console.log(`Auth check failed, retrying... (${retryCount + 1}/2)`);
        await new Promise(r => setTimeout(r, 1000));
        return fetchUser(retryCount + 1);
      } else {
        // Keep token after retries exhausted, user can refresh manually
        console.log("Auth check failed after retries, keeping token for later");
      }
    } finally {
      setLoading(false);
    }
  };

  const checkMembership = async (telegramId) => {
    try {
      const response = await axios.get(`${API}/auth/check-membership?telegram_id=${telegramId}`);
      setMembershipStatus(response.data);
    } catch (error) {
      console.error("Failed to check membership:", error);
      // Allow access if check fails (to not block users due to API errors)
      setMembershipStatus({ all_joined: true });
    }
  };

  const refreshMembership = async () => {
    if (user?.telegram_id) {
      await checkMembership(user.telegram_id);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem("hit_token", newToken);
    setToken(newToken);
    setUser(userData);
    // Check membership for new login
    if (userData?.telegram_id) {
      checkMembership(userData.telegram_id);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout?token=${token}`);
    } catch (e) {}
    localStorage.removeItem("hit_token");
    setToken(null);
    setUser(null);
    setMembershipStatus(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, fetchUser, membershipStatus, refreshMembership }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => useContext(AuthContext);

// Theme Context
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => useContext(ThemeContext);

// Login Screen
const LoginScreen = () => {
  const [telegramId, setTelegramId] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const [referralCode, setReferralCode] = useState(null);

  // Check for referral code in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      // Store in localStorage for after login
      localStorage.setItem('pending_referral', ref.toUpperCase());
    }
  }, []);

  const sendOTP = async () => {
    if (!telegramId.trim()) {
      toast.error("Enter your Telegram ID");
      return;
    }
    if (telegramId.length < 5) {
      toast.error("Invalid Telegram ID. It should be at least 5 digits.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-otp`, { telegram_id: telegramId });
      toast.success("OTP sent! Check @FreeHiterBot on Telegram");
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send OTP";
      if (errorMsg.includes("start the bot")) {
        toast.error(
          <div>
            <p className="font-bold">Start the bot first!</p>
            <p className="text-sm">Open Telegram → Search @FreeHiterBot → Click Start</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otp.trim()) {
      toast.error("Enter the OTP");
      return;
    }
    if (otp.length !== 6) {
      toast.error("OTP must be 6 digits");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        telegram_id: telegramId,
        otp: otp
      });
      
      const newToken = response.data.token;
      
      // Apply pending referral code if exists
      const pendingRef = localStorage.getItem('pending_referral');
      if (pendingRef) {
        try {
          await axios.post(`${API}/referral/apply?token=${newToken}`, {
            referral_code: pendingRef
          });
          toast.success("Referral bonus applied!");
        } catch (refError) {
          // Ignore if referral fails (might be own code or already used)
          console.log("Referral not applied:", refError.response?.data?.detail);
        }
        localStorage.removeItem('pending_referral');
        // Clear URL params
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      
      login(newToken, response.data.user);
      toast.success("Login successful!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Invalid OTP";
      if (errorMsg.includes("expired")) {
        toast.error("OTP expired. Please request a new one.");
        setStep(1);
        setOtp("");
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Referral Banner */}
        {referralCode && (
          <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-3 mb-4 text-center">
            <Gift weight="fill" className="w-5 h-5 text-cyan-500 mx-auto mb-1" />
            <p className="text-sm font-medium">You've been invited!</p>
            <p className="text-xs text-muted-foreground">Login to activate your referral bonus</p>
          </div>
        )}

        {/* Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <Lightning weight="fill" className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-black tracking-tight">HIT</h1>
          <p className="text-muted-foreground text-sm">Stripe Auto Hitter</p>
        </div>

        {/* Login Card */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <TelegramLogo weight="fill" className="w-6 h-6 text-[#0088cc]" />
            <span className="font-semibold">Login with Telegram</span>
          </div>

          {step === 1 ? (
            <div className="space-y-4">
              {/* Instructions */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 space-y-2">
                <p className="text-xs font-semibold text-blue-400">📋 Before Login:</p>
                <ol className="text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                  <li>Open Telegram and search <span className="text-blue-400 font-mono">@FreeHiterBot</span></li>
                  <li>Click Start - You'll get your User ID</li>
                  <li>Come back here and enter your ID</li>
                </ol>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Telegram User ID
                </label>
                <Input
                  type="text"
                  placeholder="123456789"
                  value={telegramId}
                  onChange={(e) => setTelegramId(e.target.value.replace(/\D/g, ""))}
                  className="text-center font-mono text-lg"
                  data-testid="telegram-id-input"
                />
              </div>
              <Button
                onClick={sendOTP}
                disabled={loading || !telegramId}
                className="w-full"
                data-testid="send-otp-btn"
              >
                {loading ? <Spinner className="w-4 h-4 animate-spin mr-2" /> : <TelegramLogo weight="fill" className="w-4 h-4 mr-2" />}
                Send OTP
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-center">
                <CheckCircle weight="fill" className="w-5 h-5 text-green-500 mx-auto mb-1" />
                <p className="text-xs text-green-400">OTP sent to your Telegram!</p>
                <p className="text-xs text-muted-foreground">Check @FreeHiterBot for the code</p>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
                  Enter 6-Digit OTP
                </label>
                <Input
                  type="text"
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="text-center font-mono text-2xl tracking-[0.5em]"
                  maxLength={6}
                  data-testid="otp-input"
                />
              </div>
              <Button
                onClick={verifyOTP}
                disabled={loading || otp.length !== 6}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="verify-otp-btn"
              >
                {loading ? <Spinner className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle weight="fill" className="w-4 h-4 mr-2" />}
                Verify & Login
              </Button>
              <button
                onClick={() => { setStep(1); setOtp(""); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
              >
                <ArrowRight weight="bold" className="w-3 h-3 rotate-180" />
                Change Telegram ID
              </button>
            </div>
          )}
        </div>

        {/* Help Link */}
        <p className="text-xs text-center text-muted-foreground mt-4">
          Need help? Contact <a href="https://t.me/TeachHit" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">@TeachHit</a>
        </p>
      </div>
    </div>
  );
};

// Force Join Screen - Requires users to join group and channel
const ForceJoinScreen = () => {
  const { membershipStatus, refreshMembership, logout, user } = useAuth();
  const [checking, setChecking] = useState(false);

  const handleCheck = async () => {
    setChecking(true);
    await refreshMembership();
    setChecking(false);
    if (membershipStatus?.all_joined) {
      toast.success("Welcome! You now have full access.");
    } else {
      toast.error("Please join both group and channel to continue.");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-primary to-primary/50 rounded-2xl flex items-center justify-center">
            <Lightning weight="fill" className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-black tracking-tight">HIT</h1>
          <p className="text-muted-foreground text-sm">Join Required Communities</p>
        </div>

        {/* Welcome */}
        <div className="bg-card border border-border rounded-2xl p-4 text-center">
          <p className="text-sm text-muted-foreground">Welcome, <span className="font-bold text-foreground">{user?.first_name || "User"}</span>!</p>
        </div>

        {/* Force Join Card */}
        <div className="bg-card border border-border rounded-2xl p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-border">
            <TelegramLogo weight="fill" className="w-6 h-6 text-[#0088cc]" />
            <span className="font-semibold">Join to Continue</span>
          </div>

          <p className="text-sm text-muted-foreground text-center">
            To use this app, you must join our Telegram group and channel first.
          </p>

          <div className="space-y-3">
            {/* Group */}
            <a
              href={membershipStatus?.group_link || "https://t.me/+rBq3QyI5Z7MzOGY9"}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                membershipStatus?.group_joined 
                  ? "bg-green-500/10 border-green-500/50" 
                  : "bg-muted/50 border-border hover:border-primary"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  membershipStatus?.group_joined ? "bg-green-500" : "bg-[#0088cc]"
                }`}>
                  {membershipStatus?.group_joined 
                    ? <CheckCircle weight="fill" className="w-5 h-5 text-white" />
                    : <Users weight="fill" className="w-5 h-5 text-white" />
                  }
                </div>
                <div>
                  <p className="font-semibold">Join Group</p>
                  <p className="text-xs text-muted-foreground">Support & Updates</p>
                </div>
              </div>
              {!membershipStatus?.group_joined && (
                <ArrowRight weight="bold" className="w-5 h-5 text-muted-foreground" />
              )}
            </a>

            {/* Channel */}
            <a
              href={membershipStatus?.channel_link || "https://t.me/TeachHit"}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                membershipStatus?.channel_joined 
                  ? "bg-green-500/10 border-green-500/50" 
                  : "bg-muted/50 border-border hover:border-primary"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  membershipStatus?.channel_joined ? "bg-green-500" : "bg-[#0088cc]"
                }`}>
                  {membershipStatus?.channel_joined 
                    ? <CheckCircle weight="fill" className="w-5 h-5 text-white" />
                    : <TelegramLogo weight="fill" className="w-5 h-5 text-white" />
                  }
                </div>
                <div>
                  <p className="font-semibold">Join Channel</p>
                  <p className="text-xs text-muted-foreground">@TeachHit</p>
                </div>
              </div>
              {!membershipStatus?.channel_joined && (
                <ArrowRight weight="bold" className="w-5 h-5 text-muted-foreground" />
              )}
            </a>
          </div>

          <Button
            onClick={handleCheck}
            disabled={checking}
            className="w-full bg-[#0088cc] hover:bg-[#0088cc]/90"
            data-testid="check-membership-btn"
          >
            {checking ? <Spinner className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle weight="bold" className="w-4 h-4 mr-2" />}
            I've Joined, Verify
          </Button>

          <button
            onClick={logout}
            className="w-full text-sm text-muted-foreground hover:text-red-500 flex items-center justify-center gap-2"
          >
            <SignOut weight="bold" className="w-4 h-4" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

// Dashboard Header
const DashboardHeader = ({ onMenuClick }) => {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  return (
    <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border">
      <div className="flex items-center justify-between px-4 h-14">
        <button onClick={onMenuClick} className="p-2 -ml-2 hover:bg-accent rounded-lg">
          <List weight="bold" className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2">
          <ChartLine weight="bold" className="w-5 h-5 text-primary" />
          <span className="font-bold">Dashboard</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="p-2 hover:bg-accent rounded-lg">
            <UserCircle weight="fill" className="w-5 h-5" />
          </button>
          <button onClick={toggleTheme} className="p-2 hover:bg-accent rounded-lg">
            {theme === "dark" ? <Sun weight="bold" className="w-5 h-5" /> : <Moon weight="bold" className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </header>
  );
};

// User Status Card
const UserStatusCard = ({ user, globalStats }) => {
  const getPlanColor = (plan) => {
    switch (plan) {
      case "Gold": return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
      case "Silver": return "bg-gray-400/20 text-gray-400 border-gray-400/30";
      default: return "bg-blue-500/20 text-blue-500 border-blue-500/30";
    }
  };

  const getRoleColor = (role) => {
    return role === "Admin" 
      ? "bg-red-500/20 text-red-500 border-red-500/30" 
      : "bg-green-500/20 text-green-500 border-green-500/30";
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <Trophy weight="fill" className="w-5 h-5 text-yellow-500" />
        <span className="font-semibold">Your Status</span>
      </div>
      
      <div className="flex items-center gap-4 p-3 bg-muted/50 rounded-xl">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg">
          {user?.first_name?.[0] || "U"}
        </div>
        <div className="flex-1">
          <div className="font-bold text-lg flex items-center gap-2">
            {user?.first_name || "User"} {user?.last_name || ""}
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded border ${getPlanColor(user?.plan)}`}>
              {user?.plan || "Free"}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded border ${getRoleColor(user?.role)}`}>
              {user?.role || "User"}
            </span>
            <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
              Rank #{user?.rank || "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stats Grid
const StatsGrid = ({ globalStats, user }) => {
  const extraCredits = user?.extra_credits || 0;
  
  return (
    <div className="space-y-3">
      {/* Extra Credits Banner - Show prominently if user has extra credits */}
      {extraCredits > 0 && (
        <div className="bg-gradient-to-r from-cyan-500/20 to-green-500/20 border border-cyan-500/30 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
              <Gift weight="fill" className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <div className="text-sm font-medium">Extra Credits</div>
              <div className="text-xs text-muted-foreground">From referrals (never expires)</div>
            </div>
          </div>
          <div className="text-3xl font-black text-cyan-500">{extraCredits}</div>
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Users weight="fill" className="w-6 h-6 text-blue-500 mx-auto mb-2" />
          <div className="text-2xl font-black">{globalStats?.total_users || 0}</div>
          <div className="text-xs text-muted-foreground">Total Users</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Lightning weight="fill" className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
          <div className="text-2xl font-black text-yellow-500">{globalStats?.total_hits || 0}</div>
          <div className="text-xs text-muted-foreground">Total Hits</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Target weight="fill" className="w-6 h-6 text-purple-500 mx-auto mb-2" />
          <div className="text-2xl font-black text-purple-500">{user?.your_hits || 0}</div>
          <div className="text-xs text-muted-foreground">Your Hits</div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4 text-center">
          <Trophy weight="fill" className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
          <div className="text-2xl font-black text-yellow-500">#{user?.your_rank || "—"}</div>
          <div className="text-xs text-muted-foreground">Your Rank</div>
        </div>
      </div>
    </div>
  );
};

// Referral Banner
const ReferralBanner = ({ user }) => {
  const { token } = useAuth();
  const [showReferral, setShowReferral] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralStats, setReferralStats] = useState(null);

  useEffect(() => {
    if (showReferral && token) {
      fetchReferralStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReferral, token]);

  const fetchReferralStats = async () => {
    try {
      const response = await axios.get(`${API}/referral/stats?token=${token}`);
      setReferralStats(response.data);
    } catch (error) {
      console.error("Failed to fetch referral stats:", error);
    }
  };

  const getReferralUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}?ref=${referralStats?.referral_code || ''}`;
  };

  const copyToClipboard = async (text) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        toast.success("Referral link copied!");
      } else {
        fallbackCopy(text);
      }
    } catch (err) {
      // Fallback for when Clipboard API is blocked
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      if (successful) {
        toast.success("Referral link copied!");
      } else {
        toast.error("Failed to copy. Please copy manually.");
      }
    } catch (err) {
      toast.error("Failed to copy. Please copy manually.");
    }
  };

  const copyReferralUrl = () => {
    if (referralStats?.referral_code) {
      copyToClipboard(getReferralUrl());
    }
  };

  const shareReferralUrl = () => {
    try {
      const url = getReferralUrl();
      const text = "Join HIT - Stripe Auto Hitter and get free credits! Use my referral link:";
      
      if (navigator.share) {
        navigator.share({
          title: 'HIT - Stripe Auto Hitter',
          text: text,
          url: url
        }).catch(() => {
          // If share fails, open Telegram share
          window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        });
      } else {
        // Fallback to Telegram share
        window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
      }
    } catch (err) {
      // Fallback to Telegram share
      const url = getReferralUrl();
      const text = "Join HIT - Stripe Auto Hitter and get free credits! Use my referral link:";
      window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (showReferral) {
    return (
      <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift weight="fill" className="w-5 h-5 text-cyan-500" />
            <span className="font-semibold">Referral System</span>
          </div>
          <button onClick={() => setShowReferral(false)} className="text-xs text-muted-foreground hover:text-foreground">
            Close
          </button>
        </div>

        {/* Your Referral URL */}
        <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-xl p-3">
          <p className="text-xs text-muted-foreground mb-2">Your Referral Link</p>
          <div className="bg-background px-3 py-2 rounded-lg mb-2 overflow-hidden">
            <code className="text-sm text-cyan-500 break-all">
              {referralStats?.referral_code ? getReferralUrl() : "Loading..."}
            </code>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={copyReferralUrl} className="flex-1">
              <Copy weight="bold" className="w-4 h-4 mr-2" />
              Copy Link
            </Button>
            <Button size="sm" onClick={shareReferralUrl} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
              <TelegramLogo weight="bold" className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2 text-center">Share to earn 2 extra credits per referral!</p>
        </div>

        {/* Referral Stats */}
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-muted/50 rounded-lg p-2">
            <div className="text-lg font-bold text-primary">{referralStats?.referral_count || 0}</div>
            <div className="text-xs text-muted-foreground">Referrals</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <div className="text-lg font-bold text-green-500">{referralStats?.extra_credits || 0}</div>
            <div className="text-xs text-muted-foreground">Extra Credits</div>
          </div>
          <div className="bg-muted/50 rounded-lg p-2">
            <div className="text-lg font-bold text-cyan-500">{referralStats?.total_earned || 0}</div>
            <div className="text-xs text-muted-foreground">Total Earned</div>
          </div>
        </div>
        
        {referralStats?.referred_by && (
          <p className="text-xs text-center text-muted-foreground">
            ✓ You were referred by someone
          </p>
        )}
      </div>
    );
  }

  return (
    <button 
      onClick={() => setShowReferral(true)}
      className="w-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl p-4 flex items-center gap-4 hover:from-cyan-500/30 hover:to-blue-500/30 transition-colors"
    >
      <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center">
        <Gift weight="fill" className="w-6 h-6 text-cyan-500" />
      </div>
      <div className="flex-1 text-left">
        <div className="font-semibold">Get Extra Credit</div>
        <div className="text-xs text-muted-foreground">Refer friends & earn 2 credits each — never expires!</div>
      </div>
      <ArrowRight weight="bold" className="w-5 h-5 text-cyan-500" />
    </button>
  );
};

// Daily Limit
const DailyLimit = ({ user }) => {
  const used = user?.daily_hits || 0;
  const limit = user?.daily_limit || 10;
  const extraCredits = user?.extra_credits || 0;
  const percentage = Math.min((used / limit) * 100, 100);
  const totalAvailable = Math.max(limit - used, 0) + extraCredits;
  
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target weight="fill" className="w-5 h-5 text-primary" />
          <span className="font-semibold">Credits</span>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-mono font-bold ${used >= limit ? 'text-yellow-500' : 'text-green-500'}`}>
            Daily: {used}/{limit}
          </span>
          {extraCredits > 0 && (
            <span className="text-sm font-mono font-bold text-cyan-500">
              +{extraCredits} Extra
            </span>
          )}
        </div>
      </div>
      
      {/* Daily Limit Progress */}
      <div className="h-3 bg-muted rounded-full overflow-hidden mb-2">
        <div 
          className={`h-full rounded-full transition-all ${used >= limit ? 'bg-yellow-500' : 'bg-green-500'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Extra Credits Bar */}
      {extraCredits > 0 && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-muted-foreground">Extra:</span>
          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-cyan-500 rounded-full" style={{ width: '100%' }} />
          </div>
          <span className="text-xs font-bold text-cyan-500">{extraCredits}</span>
        </div>
      )}
      
      <p className="text-xs text-muted-foreground mt-3 text-center">
        {totalAvailable > 0 
          ? `${totalAvailable} hit${totalAvailable !== 1 ? 's' : ''} available (daily resets, extra credits don't expire)`
          : "No credits left. Refer friends to earn more!"}
      </p>
    </div>
  );
};

// Quick Actions - Only Auto Hitter
const QuickActions = ({ onAction }) => {
  const actions = [
    { id: "charge-card", label: "Charge with Card", icon: CreditCard, color: "from-blue-600 to-blue-800" },
    { id: "charge-bin", label: "Charge with BIN", icon: Lightning, color: "from-teal-600 to-teal-800" },
  ];

  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-4">
        <Target weight="fill" className="w-5 h-5 text-teal-500" />
        <span className="font-semibold">Auto Hitter</span>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {actions.map((action) => (
          <button
            key={action.id}
            onClick={() => onAction(action.id)}
            className={`p-4 rounded-xl bg-gradient-to-br ${action.color} hover:opacity-90 transition-opacity`}
            data-testid={`action-${action.id}`}
          >
            <action.icon weight="bold" className="w-6 h-6 text-white mx-auto mb-2" />
            <div className="text-sm text-white font-medium">{action.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
};

// Charge with Card Page - Multiple Cards (1-50)
const ChargeWithCardPage = ({ onBack }) => {
  const { token } = useAuth();
  const [cards, setCards] = useState("");
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stopped, setStopped] = useState(false);

  const cardCount = cards.split("\n").filter(c => c.trim()).length;

  const handleStartCharging = async () => {
    const cardList = cards.split("\n").filter(c => c.trim());
    if (cardList.length === 0 || !checkoutUrl.trim()) {
      toast.error("Enter checkout URL and at least 1 card");
      return;
    }
    if (cardList.length > 50) {
      toast.error("Maximum 50 cards allowed");
      return;
    }

    setLoading(true);
    setResults([]);
    setCheckoutInfo(null);
    setCurrentIndex(0);
    setStopped(false);

    try {
      // Fetch checkout info first
      const infoResponse = await axios.post(`${API}/checkout/info?checkout_url=${encodeURIComponent(checkoutUrl.trim())}`);
      setCheckoutInfo(infoResponse.data);

      // Process cards one by one
      for (let i = 0; i < cardList.length; i++) {
        if (stopped) break;
        
        setCurrentIndex(i + 1);
        const card = cardList[i].trim();
        
        try {
          const response = await axios.post(`${API}/check/single${token ? `?token=${token}` : ''}`, {
            card: card,
            checkout_url: checkoutUrl.trim(),
            session_cache: infoResponse.data.session_cache
          });

          setResults(prev => [...prev, { ...response.data, card_full: card }]);

          if (response.data.status === "charged") {
            toast.success(`Card ${i + 1} Charged!`);
          }
        } catch (error) {
          setResults(prev => [...prev, { 
            card: card, 
            card_masked: card.substring(0, 6) + "******" + card.slice(-4),
            status: "error", 
            message: error.response?.data?.detail || "Failed",
            elapsed: 0
          }]);
          
          // Stop if daily limit reached
          if (error.response?.status === 429) {
            toast.error("Daily limit reached!");
            break;
          }
        }
        
        // Small delay between cards
        await new Promise(r => setTimeout(r, 500));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fetch checkout info");
    } finally {
      setLoading(false);
    }
  };

  const stopCharging = () => {
    setStopped(true);
    setLoading(false);
  };

  const exportResults = () => {
    const lines = results.map(r => `${r.card_full || r.card} | ${r.status.toUpperCase()} | ${r.message}`);
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "results.txt";
    a.click();
  };

  const charged = results.filter(r => r.status === "charged").length;
  const live = results.filter(r => r.status === "live" || r.status === "live_declined").length;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-accent rounded-lg text-sm">
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <CreditCard weight="fill" className="w-5 h-5 text-blue-500" />
            <span className="font-bold">Charge with Card</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Checkout URL
            </label>
            <Input
              type="text"
              placeholder="https://checkout.stripe.com/..."
              value={checkoutUrl}
              onChange={(e) => setCheckoutUrl(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Cards (1-50, one per line)
            </label>
            
            <Textarea
              placeholder="4111111111111111|12|25|123&#10;5500000000000004|06|26|456"
              value={cards}
              onChange={(e) => setCards(e.target.value)}
              className="font-mono text-sm min-h-[120px]"
            />
            <p className={`text-xs mt-1 ${cardCount > 50 ? 'text-red-500' : 'text-muted-foreground'}`}>
              {cardCount} card{cardCount !== 1 ? 's' : ''} {cardCount > 50 ? '(max 50)' : ''}
            </p>
          </div>

          {!loading ? (
            <Button onClick={handleStartCharging} className="w-full bg-blue-600 hover:bg-blue-700" disabled={cardCount === 0 || cardCount > 50}>
              <Lightning weight="fill" className="w-4 h-4 mr-2" />
              Start Charging ({cardCount})
            </Button>
          ) : (
            <Button onClick={stopCharging} variant="destructive" className="w-full">
              <Stop weight="fill" className="w-4 h-4 mr-2" />
              Stop ({currentIndex}/{cardCount})
            </Button>
          )}
        </div>

        {checkoutInfo && (
          <div className="bg-card border border-border rounded-xl p-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Site</span>
              <span className="font-medium">{checkoutInfo.merchant || "Unknown"}</span>
            </div>
            {checkoutInfo.product_name && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Product</span>
                <span className="font-medium">{checkoutInfo.product_name}</span>
              </div>
            )}
            {checkoutInfo.amount && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-mono font-bold text-primary">{checkoutInfo.amount} {checkoutInfo.currency}</span>
              </div>
            )}
          </div>
        )}

        {results.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex justify-between items-center p-3 border-b border-border">
              <div className="flex gap-2 text-xs">
                <span className="text-green-500">{charged} Charged</span>
                <span className="text-yellow-500">{live} Live</span>
                <span className="text-red-500">{results.length - charged - live} Dead</span>
              </div>
              <Button size="sm" variant="outline" onClick={exportResults}>
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {results.map((r, i) => (
                <div key={i} className={`px-3 py-3 border-b border-border/50 last:border-0 ${
                  r.status === "charged" ? "bg-green-500/10" : ""
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs">{r.card_masked}</span>
                    <span className={`text-xs font-bold ${
                      r.status === "charged" ? "text-green-500" :
                      r.status === "live" || r.status === "live_declined" ? "text-yellow-500" :
                      "text-red-500"
                    }`}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{r.message}</div>
                  
                  {/* Invoice for charged cards */}
                  {r.status === "charged" && (
                    <div className="mt-2 p-3 bg-green-600/30 rounded-lg border border-green-500/50">
                      <div className="flex items-center gap-2 text-green-400 text-sm font-bold mb-3">
                        <CheckCircle weight="fill" className="w-5 h-5" />
                        PAYMENT SUCCESSFUL
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Card</span>
                          <span className="font-mono text-white">{r.card_full || r.card}</span>
                        </div>
                        {(r.merchant || checkoutInfo?.merchant) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Site</span>
                            <span className="font-medium text-white">{r.merchant || checkoutInfo?.merchant}</span>
                          </div>
                        )}
                        {(r.amount || checkoutInfo?.amount) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Amount</span>
                            <span className="font-bold text-green-400">{r.amount || checkoutInfo?.amount} {r.currency || checkoutInfo?.currency}</span>
                          </div>
                        )}
                        {r.bin_info && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Card Info</span>
                            <span className="text-white">{r.bin_info.brand?.toUpperCase()} {r.bin_info.type?.toUpperCase()} {r.bin_info.country_flag}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Charge with BIN Page - Generate cards from BIN
const ChargeWithBinPage = ({ onBack }) => {
  const { token } = useAuth();
  const [bin, setBin] = useState("");
  const [cardCount, setCardCount] = useState(10);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatedCards, setGeneratedCards] = useState([]);
  const [results, setResults] = useState([]);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [stopped, setStopped] = useState(false);

  // Generate Luhn valid cards
  const generateLuhnCards = (binStr, count) => {
    const cards = [];
    for (let i = 0; i < count; i++) {
      let card = binStr.replace(/x/gi, () => Math.floor(Math.random() * 10));
      
      // Pad to 15 digits
      while (card.length < 15) {
        card += Math.floor(Math.random() * 10);
      }
      
      // Calculate Luhn check digit
      let sum = 0;
      for (let j = 0; j < card.length; j++) {
        let digit = parseInt(card[card.length - 1 - j]);
        if (j % 2 === 0) {
          digit *= 2;
          if (digit > 9) digit -= 9;
        }
        sum += digit;
      }
      const checkDigit = (10 - (sum % 10)) % 10;
      card += checkDigit;
      
      // Random expiry and CVV
      const mm = String(Math.floor(Math.random() * 12) + 1).padStart(2, '0');
      const yy = String(Math.floor(Math.random() * 6) + 25);
      const cvv = String(Math.floor(Math.random() * 900) + 100);
      
      cards.push(`${card}|${mm}|${yy}|${cvv}`);
    }
    return cards;
  };

  const handleStartCharging = async () => {
    if (!bin || bin.length < 6) {
      toast.error("Enter a valid BIN (min 6 digits)");
      return;
    }
    if (!checkoutUrl.trim()) {
      toast.error("Enter checkout URL");
      return;
    }

    setLoading(true);
    setResults([]);
    setCheckoutInfo(null);
    setCurrentIndex(0);
    setStopped(false);

    // Generate cards
    const cards = generateLuhnCards(bin, Math.min(cardCount, 50));
    setGeneratedCards(cards);
    toast.success(`Generated ${cards.length} cards from BIN`);

    try {
      // Fetch checkout info
      const infoResponse = await axios.post(`${API}/checkout/info?checkout_url=${encodeURIComponent(checkoutUrl.trim())}`);
      setCheckoutInfo(infoResponse.data);

      // Process cards
      for (let i = 0; i < cards.length; i++) {
        if (stopped) break;
        
        setCurrentIndex(i + 1);
        const card = cards[i];
        
        try {
          const response = await axios.post(`${API}/check/single${token ? `?token=${token}` : ''}`, {
            card: card,
            checkout_url: checkoutUrl.trim(),
            session_cache: infoResponse.data.session_cache
          });

          setResults(prev => [...prev, { ...response.data, card_full: card }]);

          if (response.data.status === "charged") {
            toast.success(`Card ${i + 1} Charged!`);
          }
        } catch (error) {
          setResults(prev => [...prev, { 
            card: card, 
            card_masked: card.substring(0, 6) + "******" + card.slice(-4),
            status: "error", 
            message: error.response?.data?.detail || "Failed",
            elapsed: 0
          }]);
          
          if (error.response?.status === 429) {
            toast.error("Daily limit reached!");
            break;
          }
        }
        
        await new Promise(r => setTimeout(r, 500));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const stopCharging = () => {
    setStopped(true);
    setLoading(false);
  };

  const exportResults = () => {
    const lines = results.map(r => `${r.card_full || r.card} | ${r.status.toUpperCase()} | ${r.message}`);
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bin_results.txt";
    a.click();
  };

  const charged = results.filter(r => r.status === "charged").length;
  const live = results.filter(r => r.status === "live" || r.status === "live_declined").length;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between px-4 h-14">
          <button onClick={onBack} className="p-2 -ml-2 hover:bg-accent rounded-lg text-sm">
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <Lightning weight="fill" className="w-5 h-5 text-teal-500" />
            <span className="font-bold">Charge with BIN</span>
          </div>
          <div className="w-16" />
        </div>
      </header>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        <div className="bg-card border border-border rounded-2xl p-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Checkout URL
            </label>
            <Input
              type="text"
              placeholder="https://checkout.stripe.com/..."
              value={checkoutUrl}
              onChange={(e) => setCheckoutUrl(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              BIN (use x for random)
            </label>
            <Input
              type="text"
              placeholder="374355xxxxxx"
              value={bin}
              onChange={(e) => setBin(e.target.value)}
              className="font-mono text-sm"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-2 block">
              Cards to Generate (1-50)
            </label>
            <Input
              type="number"
              min="1"
              max="50"
              placeholder="10"
              value={cardCount}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                setCardCount(Math.min(Math.max(val, 1), 50));
              }}
              className="font-mono text-sm"
            />
          </div>

          {!loading ? (
            <Button onClick={handleStartCharging} className="w-full bg-teal-600 hover:bg-teal-700">
              <Lightning weight="fill" className="w-4 h-4 mr-2" />
              Generate & Charge ({cardCount} cards)
            </Button>
          ) : (
            <Button onClick={stopCharging} variant="destructive" className="w-full">
              <Stop weight="fill" className="w-4 h-4 mr-2" />
              Stop ({currentIndex}/{generatedCards.length})
            </Button>
          )}
        </div>

        {checkoutInfo && (
          <div className="bg-card border border-border rounded-xl p-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Site</span>
              <span className="font-medium">{checkoutInfo.merchant || "Unknown"}</span>
            </div>
            {checkoutInfo.product_name && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Product</span>
                <span className="font-medium">{checkoutInfo.product_name}</span>
              </div>
            )}
            {checkoutInfo.amount && (
              <div className="flex justify-between text-sm mt-1">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-mono font-bold text-primary">{checkoutInfo.amount} {checkoutInfo.currency}</span>
              </div>
            )}
          </div>
        )}

        {results.length > 0 && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="flex justify-between items-center p-3 border-b border-border">
              <div className="flex gap-2 text-xs">
                <span className="text-green-500">{charged} Charged</span>
                <span className="text-yellow-500">{live} Live</span>
                <span className="text-red-500">{results.length - charged - live} Dead</span>
              </div>
              <Button size="sm" variant="outline" onClick={exportResults}>
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {results.map((r, i) => (
                <div key={i} className={`px-3 py-3 border-b border-border/50 last:border-0 ${
                  r.status === "charged" ? "bg-green-500/10" : ""
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs">{r.card_masked}</span>
                    <span className={`text-xs font-bold ${
                      r.status === "charged" ? "text-green-500" :
                      r.status === "live" || r.status === "live_declined" ? "text-yellow-500" :
                      "text-red-500"
                    }`}>
                      {r.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{r.message}</div>
                  
                  {/* Invoice for charged cards */}
                  {r.status === "charged" && (
                    <div className="mt-2 p-3 bg-green-600/30 rounded-lg border border-green-500/50">
                      <div className="flex items-center gap-2 text-green-400 text-sm font-bold mb-3">
                        <CheckCircle weight="fill" className="w-5 h-5" />
                        PAYMENT SUCCESSFUL
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Card</span>
                          <span className="font-mono text-white">{r.card_full || r.card}</span>
                        </div>
                        {(r.merchant || checkoutInfo?.merchant) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Site</span>
                            <span className="font-medium text-white">{r.merchant || checkoutInfo?.merchant}</span>
                          </div>
                        )}
                        {(r.amount || checkoutInfo?.amount) && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Amount</span>
                            <span className="font-bold text-green-400">{r.amount || checkoutInfo?.amount} {r.currency || checkoutInfo?.currency}</span>
                          </div>
                        )}
                        {r.bin_info && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Card Info</span>
                            <span className="text-white">{r.bin_info.brand?.toUpperCase()} {r.bin_info.type?.toUpperCase()} {r.bin_info.country_flag}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Result Card Component
const ResultCard = ({ result, checkoutInfo }) => {
  const isSuccess = result.status === "charged";
  const isLive = result.status === "live" || result.status === "live_declined";

  return (
    <div className={`rounded-xl border overflow-hidden ${
      isSuccess ? "border-green-500/50 bg-green-500/10" :
      isLive ? "border-yellow-500/50 bg-yellow-500/10" :
      "border-red-500/50 bg-red-500/10"
    }`}>
      <div className={`px-4 py-3 flex items-center gap-3 ${
        isSuccess ? "bg-green-500/20" :
        isLive ? "bg-yellow-500/20" :
        "bg-red-500/20"
      }`}>
        {isSuccess ? <CheckCircle weight="fill" className="w-6 h-6 text-green-500" /> :
         isLive ? <Lightning weight="fill" className="w-6 h-6 text-yellow-500" /> :
         <XCircle weight="fill" className="w-6 h-6 text-red-500" />}
        <div>
          <div className={`font-bold ${
            isSuccess ? "text-green-500" :
            isLive ? "text-yellow-500" :
            "text-red-500"
          }`}>
            {isSuccess ? "CHARGED" : isLive ? "LIVE" : "DECLINED"}
          </div>
          <div className="text-xs text-muted-foreground">{result.elapsed}s</div>
        </div>
      </div>
      <div className="px-4 py-3 space-y-2">
        <div className="flex justify-between items-center">
          <span className="font-mono text-sm">{result.card_masked}</span>
          {checkoutInfo?.amount && (
            <span className="font-mono font-bold">{checkoutInfo.amount} {checkoutInfo.currency}</span>
          )}
        </div>
        
        {/* Site Name */}
        {checkoutInfo?.merchant && (
          <div className="flex items-center gap-2 text-sm">
            <Storefront weight="fill" className="w-4 h-4 text-primary" />
            <span className="font-medium">{checkoutInfo.merchant}</span>
          </div>
        )}
        
        <div className="text-xs text-muted-foreground">{result.message}</div>
        
        {result.bin_info && (
          <div className="flex gap-2 text-xs text-muted-foreground">
            <span>{result.bin_info.brand}</span>
            <span>•</span>
            <span>{result.bin_info.type}</span>
            <span>•</span>
            <span>{result.bin_info.country_flag} {result.bin_info.country_code}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Dashboard
const Dashboard = () => {
  const { user, token, logout } = useAuth();
  const [globalStats, setGlobalStats] = useState({});
  const [currentPage, setCurrentPage] = useState("dashboard");
  const [menuOpen, setMenuOpen] = useState(false);

  const fetchGlobalStats = useCallback(async () => {
    try {
      const url = token ? `${API}/global-stats?token=${token}` : `${API}/global-stats`;
      const response = await axios.get(url);
      setGlobalStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchGlobalStats();
    const interval = setInterval(fetchGlobalStats, 30000);
    return () => clearInterval(interval);
  }, [fetchGlobalStats]);

  const handleAction = (actionId) => {
    if (actionId === "charge-card") {
      setCurrentPage("charge-card");
    } else if (actionId === "charge-bin") {
      setCurrentPage("charge-bin");
    }
  };

  if (currentPage === "charge-card") {
    return <ChargeWithCardPage onBack={() => setCurrentPage("dashboard")} />;
  }
  
  if (currentPage === "charge-bin") {
    return <ChargeWithBinPage onBack={() => setCurrentPage("dashboard")} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuClick={() => setMenuOpen(!menuOpen)} />
      
      <div className="p-4 space-y-4 max-w-lg mx-auto pb-20">
        {/* User Status */}
        <UserStatusCard user={user} globalStats={globalStats} />
        
        {/* Stats Grid */}
        <StatsGrid globalStats={globalStats} user={globalStats.user} />
        
        {/* Referral Banner */}
        <ReferralBanner user={globalStats.user} />
        
        {/* Daily Limit */}
        <DailyLimit user={globalStats.user} />
        
        {/* Quick Actions */}
        <QuickActions onAction={handleAction} />

        {/* Logout */}
        <button
          onClick={logout}
          className="w-full p-4 bg-card border border-border rounded-xl flex items-center justify-center gap-2 text-red-500 hover:bg-red-500/10 transition-colors"
          data-testid="logout-btn"
        >
          <SignOut weight="bold" className="w-5 h-5" />
          Logout
        </button>
      </div>
    </div>
  );
};

// Error Boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <div className="text-center">
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Disclaimer Modal
const DisclaimerModal = ({ onAccept }) => {
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm overflow-y-auto">
      <div className="min-h-full flex items-start sm:items-center justify-center p-3 sm:p-4 py-4 sm:py-6">
        <div className="w-full max-w-md bg-gradient-to-b from-[#1a1a2e] to-[#0f0f1a] border border-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[90vh]">
          {/* Header - Fixed */}
          <div className="p-4 sm:p-6 pb-3 sm:pb-4 flex-shrink-0">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                <ShieldWarning weight="fill" className="w-5 h-5 sm:w-7 sm:h-7 text-black" />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white">Disclaimer</h2>
                <p className="text-gray-400 text-xs sm:text-sm">Please read carefully</p>
              </div>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="px-4 sm:px-6 space-y-3 sm:space-y-4 overflow-y-auto flex-1 min-h-0">
            {/* Educational Purpose */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 sm:p-4">
              <div className="flex gap-2 sm:gap-3">
                <Info weight="fill" className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm sm:text-base">
                  <span className="font-bold text-white">Educational Purpose Only: </span>
                  <span className="text-gray-300">This tool is developed solely for educational and research purposes to understand payment gateway security mechanisms.</span>
                </div>
              </div>
            </div>

            {/* No Illegal Activities */}
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 sm:p-4">
              <div className="flex gap-2 sm:gap-3">
                <Warning weight="fill" className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm sm:text-base">
                  <span className="font-bold text-white">No Illegal Activities: </span>
                  <span className="text-gray-300">We strictly prohibit and do not support any illegal activities, fraud, or unauthorized transactions. Any misuse of this tool is against our terms.</span>
                </div>
              </div>
            </div>

            {/* User Responsibility */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 sm:p-4">
              <div className="flex gap-2 sm:gap-3">
                <Scales weight="fill" className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm sm:text-base">
                  <span className="font-bold text-white">User Responsibility: </span>
                  <span className="text-gray-300">By using this tool, you acknowledge that you are solely responsible for your actions. The developers and operators bear no liability for any misuse or consequences arising from the use of this service.</span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="p-4 sm:p-6 pt-3 sm:pt-4 flex-shrink-0">
            <p className="text-center text-gray-400 text-xs sm:text-sm mb-3 sm:mb-4">
              By clicking "I Understand & Accept", you confirm that you have read, understood, and agree to abide by these terms.
            </p>
            <button
              onClick={onAccept}
              className="w-full py-3 sm:py-4 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-xl flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              data-testid="accept-disclaimer-btn"
            >
              <CheckCircle weight="bold" className="w-4 h-4 sm:w-5 sm:h-5" />
              I Understand & Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppContent />
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

const AppContent = () => {
  const { user, loading, membershipStatus } = useAuth();
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  const handleAcceptDisclaimer = () => {
    setDisclaimerAccepted(true);
  };

  // Show disclaimer first (every time app opens)
  if (!disclaimerAccepted) {
    return <DisclaimerModal onAccept={handleAcceptDisclaimer} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Spinner className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <LoginScreen />;
  }

  // Logged in but hasn't joined required communities
  if (membershipStatus && !membershipStatus.all_joined) {
    return <ForceJoinScreen />;
  }

  // Logged in and joined - show dashboard
  return <Dashboard />;
};

export default App;
